import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Mic, FileText, Link2, MessageSquare, Phone, Settings, CheckCircle2, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import TrialSignupModal from '../components/TrialSignupModal';
import VoiceCallWidget from '../components/VoiceCallWidget';
import CustomChatWidget from '../components/CustomChatWidget';

const fadeIn = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.6 } }
};

const slideUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
};

const stagger = {
  visible: {
    transition: {
      staggerChildren: 0.1
    }
  }
};

const ExperiencePage = () => {
  const navigate = useNavigate();
  const [showOnboardingModal, setShowOnboardingModal] = useState(false);
  const [activeStep, setActiveStep] = useState(1);

  const steps = [
    {
      number: 1,
      icon: Mic,
      title: "Clone your voice",
      headline: "30 seconds to immortality",
      description: "Read one paragraph. Our AI captures your unique voice signature—tone, pace, personality. The clone is so accurate, your mother couldn't tell the difference.",
      script: '"Hi, this is [your name]. I\'m currently unavailable but my AI assistant can help you. They have access to all my information and can answer questions just like I would. How can I help you today?"',
      reassurance: "Your voice data is encrypted, private, and never shared. This is your voice, for your double, period."
    },
    {
      number: 2,
      icon: FileText,
      title: "Upload your knowledge",
      headline: "Pour your brain into your double",
      description: "Your double needs to know what you know. Upload documents, paste FAQs, share websites. Every piece of knowledge makes your double more you.",
      knowledgeTypes: [
        { icon: FileText, label: "Documents", desc: "Proposals, guides, everything you've written" },
        { icon: Link2, label: "Websites", desc: "Your site, LinkedIn, published content" },
        { icon: MessageSquare, label: "Instructions", desc: "Specific responses to common questions" },
        { icon: Settings, label: "Personality notes", desc: "How you handle different situations" }
      ]
    },
    {
      number: 3,
      icon: Settings,
      title: "Set your style",
      headline: "Fine-tune your professional personality",
      description: "Adjust until it sounds exactly like you on your best day.",
      sliders: [
        { label: "Formality", left: "Board meeting", right: "Coffee chat" },
        { label: "Detail Level", left: "Brief answers", right: "Thorough explanations" },
        { label: "Energy", left: "Calm presence", right: "Enthusiastic advocate" }
      ]
    },
    {
      number: 4,
      icon: Phone,
      title: "Go live instantly",
      headline: "Your double is ready for duty",
      description: "Here's your dedicated phone number. Share it anywhere—business cards, email signatures, websites. Your double is now handling calls 24/7.",
      cta: "Call your double right now. Have a conversation. Be amazed by yourself."
    }
  ];

  const capabilities = [
    {
      title: "Answer Product Questions",
      conversation: [
        { speaker: "Caller", message: "Does this integrate with Salesforce?" },
        { speaker: "Double", message: "Yes, we have native Salesforce integration. It syncs contacts, deals, and activities automatically. Takes about 10 minutes to set up." }
      ]
    },
    {
      title: "Schedule Meetings",
      conversation: [
        { speaker: "Caller", message: "Can we set up a demo this week?" },
        { speaker: "Double", message: "Absolutely! I have Thursday at 2 PM or Friday at 10 AM Eastern available. Which works better for you?" }
      ]
    },
    {
      title: "Qualify Leads",
      conversation: [
        { speaker: "Caller", message: "I'm interested in your services." },
        { speaker: "Double", message: "Excellent! To ensure we're the right fit, can you tell me about your team size and main use case?" }
      ]
    },
    {
      title: "Provide Support",
      conversation: [
        { speaker: "Caller", message: "I'm having trouble with the setup." },
        { speaker: "Double", message: "I can help with that. Let's start with where you're stuck. Are you at the voice cloning step or knowledge upload?" }
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="fixed w-full bg-white/95 backdrop-blur-sm z-50 border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <button onClick={() => navigate('/')} className="text-2xl font-bold text-gray-900">
              DoubleMe
            </button>
            <div className="flex items-center gap-4">
              <button onClick={() => navigate('/pricing')} className="text-gray-600 hover:text-gray-900 font-medium transition">
                Pricing
              </button>
              <button
                onClick={() => setShowOnboardingModal(true)}
                className="bg-primary hover:bg-primary/90 text-white px-4 py-2 rounded-lg font-medium transition"
              >
                Start Free Trial
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="pt-32 pb-12 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div initial="hidden" animate="visible" variants={stagger}>
            <motion.h1 variants={slideUp} className="text-5xl md:text-6xl font-bold text-gray-900 mb-4">
              Meet your double
            </motion.h1>
            <motion.p variants={slideUp} className="text-xl text-gray-600 mb-8">
              Let's create one together. 60 seconds. No magic. Just technology that feels like it.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Interactive Setup Flow */}
      <section className="py-12 px-4">
        <div className="max-w-5xl mx-auto">
          {/* Step Progress Indicator */}
          <div className="flex justify-center mb-12">
            <div className="flex items-center gap-2">
              {steps.map((step, index) => (
                <React.Fragment key={step.number}>
                  <button
                    onClick={() => setActiveStep(step.number)}
                    className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold transition ${
                      activeStep === step.number
                        ? 'bg-primary text-white'
                        : activeStep > step.number
                        ? 'bg-green-500 text-white'
                        : 'bg-gray-200 text-gray-600'
                    }`}
                  >
                    {activeStep > step.number ? <CheckCircle2 className="w-5 h-5" /> : step.number}
                  </button>
                  {index < steps.length - 1 && (
                    <div className={`w-12 h-1 ${activeStep > step.number ? 'bg-green-500' : 'bg-gray-200'}`} />
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>

          {/* Step Content */}
          {steps.map((step) => (
            <motion.div
              key={step.number}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: activeStep === step.number ? 1 : 0, x: activeStep === step.number ? 0 : 20 }}
              className={activeStep === step.number ? 'block' : 'hidden'}
            >
              <div className="bg-white border-2 border-dark rounded-2xl p-8 mb-8">
                {/* Step Header */}
                <div className="flex items-center gap-4 mb-6">
                  <div className="bg-secondary/10 p-4 rounded-lg">
                    <step.icon className="w-8 h-8 text-primary" />
                  </div>
                  <div>
                    <h2 className="text-3xl font-bold text-gray-900">{step.headline}</h2>
                    <p className="text-gray-600">{step.description}</p>
                  </div>
                </div>

                {/* Step 1: Voice Recording */}
                {step.number === 1 && (
                  <div className="space-y-6">
                    <div className="bg-gray-50 rounded-xl p-6">
                      <div className="flex items-start gap-4 mb-4">
                        <Mic className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
                        <div>
                          <h3 className="font-semibold text-gray-900 mb-2">Read this script:</h3>
                          <p className="text-gray-700 leading-relaxed">{step.script}</p>
                        </div>
                      </div>
                      <button className="w-full bg-primary hover:bg-primary/90 text-white px-6 py-3 rounded-lg font-semibold transition flex items-center justify-center gap-2">
                        <Mic className="w-5 h-5" />
                        Start Recording
                      </button>
                    </div>
                    <p className="text-sm text-gray-600 text-center">{step.reassurance}</p>
                  </div>
                )}

                {/* Step 2: Knowledge Upload */}
                {step.number === 2 && step.knowledgeTypes && (
                  <div className="grid md:grid-cols-2 gap-4">
                    {step.knowledgeTypes.map((type, index) => (
                      <div key={index} className="bg-gray-50 rounded-xl p-6 hover:bg-primary/5 transition cursor-pointer">
                        <type.icon className="w-8 h-8 text-primary mb-3" />
                        <h3 className="font-semibold text-gray-900 mb-2">{type.label}</h3>
                        <p className="text-sm text-gray-600">{type.desc}</p>
                      </div>
                    ))}
                  </div>
                )}

                {/* Step 3: Style Settings */}
                {step.number === 3 && step.sliders && (
                  <div className="space-y-6">
                    {step.sliders.map((slider, index) => (
                      <div key={index}>
                        <label className="block text-gray-700 font-medium mb-3">{slider.label}:</label>
                        <div className="flex items-center gap-4">
                          <span className="text-sm text-gray-600 w-32 text-right">{slider.left}</span>
                          <input type="range" className="flex-1" defaultValue="50" />
                          <span className="text-sm text-gray-600 w-32">{slider.right}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Step 4: Go Live */}
                {step.number === 4 && (
                  <div className="space-y-6">
                    <div className="text-center mb-8">
                      <p className="text-xl text-gray-700">
                        This is what it feels like to interact with a double. Try talking to ours:
                      </p>
                    </div>

                    {/* Voice and Text Demo Grid */}
                    <div className="grid md:grid-cols-2 gap-6">
                      {/* Text Chat Widget */}
                      <div className="bg-gradient-to-br from-primary/5 to-white border-2 border-dark rounded-2xl p-6">
                        <div className="flex items-center gap-3 mb-4">
                          <div className="bg-secondary/20 p-3 rounded-lg">
                            <MessageSquare className="w-6 h-6 text-primary" />
                          </div>
                          <h3 className="text-2xl font-bold text-gray-900">Text our double</h3>
                        </div>
                        <div className="bg-white rounded-xl border border-gray-200 h-[500px]">
                          <CustomChatWidget agentId="l9pLCKXTRrvyoWkGMRsK" />
                        </div>
                      </div>

                      {/* Voice Call Widget */}
                      <div className="bg-gradient-to-br from-green-50 to-white border-2 border-green-200 rounded-2xl p-6">
                        <div className="flex items-center gap-3 mb-4">
                          <div className="bg-green-100 p-3 rounded-lg">
                            <Phone className="w-6 h-6 text-green-600" />
                          </div>
                          <h3 className="text-2xl font-bold text-gray-900">Call our double</h3>
                        </div>
                        <div className="h-[500px] flex items-center">
                          <VoiceCallWidget agentId="l9pLCKXTRrvyoWkGMRsK" className="w-full" />
                        </div>
                      </div>
                    </div>

                    {/* Call-to-Action */}
                    <div className="bg-gradient-to-r from-primary/10 to-blue-50 border border-dark rounded-2xl p-8 text-center">
                      <p className="text-xl text-gray-700 mb-4">
                        That wasn't a chatbot. That's AI being you, better than you expected.
                      </p>
                      <p className="text-lg text-gray-600 mb-6">
                        Ready to create your own double?
                      </p>
                      <button
                        onClick={() => setShowOnboardingModal(true)}
                        className="bg-primary hover:bg-primary/90 text-white px-8 py-4 rounded-full font-semibold text-lg transition inline-flex items-center gap-2 shadow-lg hover:shadow-xl"
                      >
                        Start Your Free Trial
                        <ArrowRight className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                )}

                {/* Navigation */}
                {activeStep < 4 && (
                  <div className="flex justify-between mt-8">
                    <button
                      onClick={() => setActiveStep(Math.max(1, activeStep - 1))}
                      disabled={activeStep === 1}
                      className="px-6 py-2 text-gray-600 hover:text-gray-900 font-medium transition disabled:opacity-50"
                    >
                      ← Previous
                    </button>
                    <button
                      onClick={() => setActiveStep(Math.min(4, activeStep + 1))}
                      className="px-6 py-2 bg-primary hover:bg-primary/90 text-white rounded-lg font-medium transition"
                    >
                      Next Step →
                    </button>
                  </div>
                )}
                {activeStep === 4 && (
                  <div className="flex justify-center mt-8">
                    <button
                      onClick={() => setActiveStep(1)}
                      className="px-6 py-2 text-gray-600 hover:text-gray-900 font-medium transition"
                    >
                      ← Start Over
                    </button>
                  </div>
                )}
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* What Doubles Can Do */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={stagger}
            className="text-center mb-12"
          >
            <motion.h2 variants={slideUp} className="text-4xl font-bold text-gray-900 mb-4">
              What can doubles do?
            </motion.h2>
            <motion.p variants={slideUp} className="text-xl text-gray-600">
              Everything you do on calls, but simultaneously and tirelessly.
            </motion.p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-6">
            {capabilities.map((capability, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-xl border border-gray-200 p-6"
              >
                <h3 className="text-xl font-semibold text-gray-900 mb-4">{capability.title}</h3>
                <div className="space-y-3">
                  {capability.conversation.map((message, msgIndex) => (
                    <div
                      key={msgIndex}
                      className={`p-4 rounded-lg ${
                        message.speaker === 'Caller' ? 'bg-gray-50' : 'bg-primary/5'
                      }`}
                    >
                      <div className="text-xs font-semibold text-gray-500 mb-1">{message.speaker}:</div>
                      <div className="text-gray-700">{message.message}</div>
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* The Multiplication Effect */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={stagger}
          >
            <motion.h2 variants={slideUp} className="text-4xl font-bold text-gray-900 mb-6">
              Imagine having a team of you
            </motion.h2>
            <motion.p variants={slideUp} className="text-xl text-gray-600 mb-12">
              Each handling different conversations. All representing you perfectly. None burning out. Ever.
            </motion.p>

            <motion.div variants={stagger} className="space-y-6">
              {[
                { count: "2 Doubles", capability: "You're in the meeting AND taking the sales call." },
                { count: "5 Doubles", capability: "You're handling support, sales, scheduling, and qualifying simultaneously." },
                { count: "10 Doubles", capability: "You're running a full operation without adding headcount." },
                { count: "Unlimited", capability: "You're building an empire of expertise that never sleeps." }
              ].map((scale, index) => (
                <motion.div
                  key={index}
                  variants={slideUp}
                  className="bg-gradient-to-r from-primary/5 to-blue-50 border border-dark rounded-xl p-6 text-left"
                >
                  <div className="font-semibold text-primary mb-2">At {scale.count}:</div>
                  <div className="text-gray-700 text-lg">{scale.capability}</div>
                </motion.div>
              ))}
            </motion.div>

            <motion.div variants={slideUp} className="mt-12 p-8 bg-gray-900 rounded-2xl text-white">
              <p className="text-xl mb-6">
                This isn't about replacing humans. It's about amplifying humanity. Your knowledge, available to everyone who needs it. Your expertise, scaling without limits. Your impact, multiplied infinitely.
              </p>
              <p className="text-2xl font-bold text-purple-400">Welcome to being superhuman.</p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-primary text-white">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-4">Ready to multiply yourself?</h2>
          <p className="text-xl mb-8 opacity-90">Start your 14-day free trial. No credit card required.</p>
          <button
            onClick={() => setShowOnboardingModal(true)}
            className="bg-white hover:bg-gray-100 text-primary px-8 py-4 rounded-full font-semibold text-lg transition"
          >
            Create Your First Double
          </button>
        </div>
      </section>

      {/* Onboarding Modal */}
      {/* Trial Signup Modal */}
      <TrialSignupModal isOpen={showOnboardingModal} onClose={() => setShowOnboardingModal(false)} />
    </div>
  );
};

export default ExperiencePage;
